import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import outdoor from "@/assets/azzaro-outdoor.webp";

const EASE = [0.22, 1, 0.36, 1];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.25 } },
};
const lineChild = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.05, ease: EASE } },
};

const Line = ({ children }) => (
  <span className="line-mask">
    <motion.span variants={lineChild} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative mx-auto max-w-6xl px-6 pt-16 pb-16 sm:pt-24 sm:pb-20"
    >
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-8">
        {/* Text column */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="eyebrow"
            data-testid="hero-eyebrow"
          >
            Azzaro Resorts &amp; Spa — Diu
          </motion.p>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            data-testid="hero-headline"
            className="font-display mt-7 text-[13vw] leading-[0.92] tracking-tight text-[#1a1a1a] sm:text-6xl lg:text-[5.4rem]"
            style={{ fontWeight: 800 }}
          >
            <Line>We are quietly</Line>
            <Line>
              <span className="text-[#1f938b]">reimagining</span>
            </Line>
            <Line>every detail.</Line>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="font-body mt-8 max-w-[34rem] text-lg leading-relaxed text-[#4a4a4a] sm:text-xl"
            data-testid="hero-subtext"
          >
            Our official website is currently under renewal. We are rebuilding
            the Azzaro experience entirely in-house — thoughtfully, and from the
            ground up. Thank you for your patience while we craft something worth
            the wait.
          </motion.p>

          <motion.a
            href="#notices"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="group mt-12 inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#333333]"
            data-testid="hero-scroll-cue"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#2ca8a0]/40 text-[#2ca8a0] transition-transform duration-500 group-hover:translate-y-1">
              <ArrowDown className="h-4 w-4" />
            </span>
            <span className="link-underline pb-1">Important information</span>
          </motion.a>
        </div>

        {/* Image column */}
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
            className="relative aspect-[4/5] w-full overflow-hidden rounded-[1.5rem] shadow-[0_30px_80px_-30px_rgba(44,168,160,0.45)]"
            data-testid="hero-image"
          >
            <motion.img
              style={{ y: imgY, scale }}
              src={outdoor}
              alt="Azzaro Resorts & Spa — poolside courtyard at dusk"
              className="absolute inset-0 h-[128%] w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
